import React, { useState, useEffect } from "react";
import { Row, Col, Button, Spinner, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeadWithSubHead from "../../common/HeadWithSubHead";
import FileUploader from "../onboarding/studentDetails/fileUpload";
import CommonButton from "../CommonButton/CommonButton";
import { useMyContext } from "../../myContext";
import {
  documentPurposeUpload,
  paymentLink,
  getDocToBeUpload,
  reUploadOfDocuments,
  getTransactionDetailUsingId,
  generateA2SignApi,
  getUploadedDocuments,
  submitUploadedDocuments,
} from "../../api";
import tickIcon from "../../public/assets/Eo_circle_green_checkmark.svg";
import crossIcon from "../../public/assets/icons-cross.svg";
import styles from "./doc.module.css";

// ============================================================================
// CONSTANTS
// ============================================================================
const ACCEPTED_FILE_FORMATS = ".jpeg, .jpg, .png, .pdf";
const MAX_FILE_SIZE = 5000000; // 5MB
const POLLING_INTERVAL = 3000; // 3 seconds
const REDIRECT_DELAY = 5000; // 5 seconds
const SUCCESS_MESSAGE_DELAY = 4000; // 4 seconds

// Special purpose ID for student visa requirement
const STUDENT_ABROAD_PURPOSE_ID = "7b9ff313-9ff4-4241-959b-be25647e7327";

// Transaction statuses
const TRANSACTION_STATUS = {
  MANUAL_REVIEW: "MANUAL_REVIEW",
  VERIFIED: "Verified",
  A2_E_SIGNED: "A2_E_SIGNED",
  CANCELLED: "CANCELLED",
  WAITING_FOR_FUND: "WAITING_FOR_FUND",
  REJECTED: "DOCUMENTS_REJECTED",
};

// Document types
const DOC_TYPES = {
  PASSPORT_FRONT: "PASSPORT_FRONT",
  PASSPORT_BACK: "PASSPORT_BACK",
  PASSPORT: "PASSPORT",
  STUDENT_VISA: "Student's VISA",
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const DocUpload = ({ reUploadDocumentsRequired, isReupload = false }) => {
  const navigate = useNavigate();
  const { themeColor, redirectionLink } = useMyContext("");
  const isQrRequired = process.env.REACT_APP_QRCODE_REQUIRED;

  // Session data
  const [studentUserId, setStudentUserId] = useState();
  const [transId, setTransId] = useState();
  const [purpose, setPurpose] = useState();
  const [subPurpose, setSubPurpose] = useState();
  const [purposeId, setPurposeId] = useState();

  // Document data
  const [docRequired, setDocRequired] = useState([]);
  const [requiredDoc, setRequiredDoc] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Workflow states
  const [generatedA2, setGeneratedA2] = useState(false);
  const [a2SignUrl, setA2signUrl] = useState("");
  const [showButton, setShowButton] = useState(true);
  const [paymentLinkGenerated, setPaymentLinkGenerated] = useState(false);
  const [showPayNowButton, setPayNowButton] = useState(false);
  const [messageToDisplay, setMessageToDisplay] = useState("");
  const [transactionStatusIcon, setTransactionStatusIcon] = useState("");
  const [showNavigateToTransactionButton, setShowNavigateToTransactionButton] =
    useState(false);

  // Special cases
  const [visaCheckBox, setVisaCheckBox] = useState(false);
  const [showStudentVisa, setShowStudentVisa] = useState(true);
  const [isManualReview, setIsManualReview] = useState(false);

  useEffect(() => {
    sessionStorage.setItem("Reupload", isReupload);
  }, [isReupload]);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  useEffect(() => {
    initializeSessionData();
    if (isReupload !== true) {
      fetchUploadedDocuments();
    }
  }, []);

  const initializeSessionData = () => {
    setStudentUserId(sessionStorage.getItem("studentId"));
    setTransId(sessionStorage.getItem("transactionID"));
    setPurpose(sessionStorage.getItem("purpose"));
    setSubPurpose(sessionStorage.getItem("subPurpose"));
    setPurposeId(sessionStorage.getItem("purposeId"));
  };

  const fetchUploadedDocuments = async () => {
    try {
      const studentId = sessionStorage.getItem("studentId");
      const response = await getUploadedDocuments(studentId);
      const docs = response?.data || [];
      setUploadedDocs(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.error("Error fetching uploaded documents:", error);
      setUploadedDocs([]);
    }
  };

  // ============================================================================
  // FETCH REQUIRED DOCUMENTS
  // ============================================================================
  useEffect(() => {
    if (transId) {
      fetchRequiredDocuments();
    }
  }, [transId]);

  const fetchRequiredDocuments = async () => {
    setLoading(true);
    try {
      const response = await getDocToBeUpload(transId);
      if (response.status === 200 && response.data.length > 0) {
        // Filter out "Form A2" (A2_FORM) document
        const filteredDocs = response.data.filter(
          (doc) => doc.docType !== "A2_FORM"
        );
        setRequiredDoc(filteredDocs);
      }
    } catch (error) {
      console.error("Error fetching required documents:", error);
    }
  };

  // ============================================================================
  // PREPARE DOCUMENTS FOR RE-UPLOAD
  // ============================================================================
  useEffect(() => {
    if (isReupload && requiredDoc.length > 0 && docRequired.length === 0) {
      prepareReuploadDocuments();
    }
  }, [isReupload, reUploadDocumentsRequired, requiredDoc]);

  const prepareReuploadDocuments = () => {
    setLoading(false);

    const convertedData = [];

    reUploadDocumentsRequired.forEach((doc) => {
      const matchedDoc = requiredDoc.find(
        (item) => item.docType === doc.doctype
      );
      const docName = matchedDoc ? matchedDoc.docName : doc.doctype;

      // Handle REMITTER_PASSPORT
      if (doc.doctype === "REMITTER_PASSPORT") {
        const sideLabel = doc.side === "front" ? "Front" : "Back";
        convertedData.push({
          name: `Remitter's Passport (${sideLabel})`,
          label: `REMITTER_PASSPORT_${doc.side.toUpperCase()}`,
          file: [],
          isRequired: true,
          internalLabel: "REMITTER_PASSPORT",
          docUpdate: "",
          side: doc.side,
        });
      }
      // Handle STUDENT_PASSPORT
      else if (doc.doctype === "STUDENT_PASSPORT") {
        const sideLabel = doc.side === "front" ? "Front" : "Back";
        convertedData.push({
          name: `Student's Passport (${sideLabel})`,
          label: `STUDENT_PASSPORT_${doc.side.toUpperCase()}`,
          file: [],
          isRequired: true,
          internalLabel: "STUDENT_PASSPORT",
          docUpdate: "",
          side: doc.side,
        });
      }
      // Handle REMITTER_PAN
      else if (doc.doctype === "REMITTER_PAN") {
        convertedData.push({
          name: "Remitter's PAN (Front)",
          label: "REMITTER_PAN_FRONT",
          file: [],
          isRequired: true,
          internalLabel: "REMITTER_PAN",
          docUpdate: "",
          side: doc.side || "front",
        });
      }
      // Handle STUDENT_PAN
      else if (doc.doctype === "REMITTER_AADHAAR") {
        const sideLabel = doc.side === "front" ? "Front" : "Back";
        convertedData.push({
          name: `Remitter aadhaar   (${sideLabel})`,
          label: `REMITTER_AADHAAR_${doc.side.toUpperCase()}`,
          file: [],
          isRequired: true,
          internalLabel: "REMITTER_AADHAAR",
          docUpdate: "",
          side: doc.side || "front",
        });
      } else if (doc.doctype === "STUDENT_PAN") {
        convertedData.push({
          name: "Student's PAN (Front)",
          label: "STUDENT_PAN_FRONT",
          file: [],
          isRequired: true,
          internalLabel: "STUDENT_PAN",
          docUpdate: "",
          side: doc.side || "front",
        });
      }
      // Normal flow for other document types
      else {
        convertedData.push({
          name: docName,
          label: doc.doctype,
          file: [],
          isRequired: true,
          internalLabel: doc.doctype,
          docUpdate: "",
        });
      }
    });

    const hasStudentVisa = convertedData.some(
      (doc) => doc.name === DOC_TYPES.STUDENT_VISA
    );
    setShowStudentVisa(hasStudentVisa);
    setDocRequired(convertedData);
  };

  // ============================================================================
  // PREPARE DOCUMENTS FOR INITIAL UPLOAD
  // ============================================================================
  useEffect(() => {
    if (docRequired.length === 0 && requiredDoc.length > 0 && !isReupload) {
      prepareInitialDocuments();
    }
  }, [docRequired, requiredDoc, isReupload]);

  const prepareInitialDocuments = () => {
    setLoading(false);

    // Separate required and optional documents
    const requiredDocs = requiredDoc.filter(
      (doc) => !doc.isOptional || doc.docName === DOC_TYPES.STUDENT_VISA
    );
    const optionalDocs = requiredDoc.filter(
      (doc) => doc.isOptional && doc.docName !== DOC_TYPES.STUDENT_VISA
    );

    // Sort documents: required first, then optional
    const sortedDocs = [...requiredDocs, ...optionalDocs].map((doc) => ({
      name: doc.docName,
      label: doc.docType,
      file: [],
      isRequired: !doc.isOptional,
      internalLabel: doc.docType,
      docUpdate: "",
    }));

    setDocRequired(sortedDocs);
  };

  // ============================================================================
  // MARK PREVIOUSLY UPLOADED DOCUMENTS
  // ============================================================================
  useEffect(() => {
    if (uploadedDocs.length > 0) {
      markPreviouslyUploadedDocs();
    }
  }, [uploadedDocs]);

  const markPreviouslyUploadedDocs = () => {
    setDocRequired((prevDocRequired) =>
      prevDocRequired.map((doc) => {
        const matchingUploadedDoc = uploadedDocs?.find(
          (uploadedDoc) => uploadedDoc.docType === doc.internalLabel
        );

        if (matchingUploadedDoc) {
          return { ...doc, docUpdate: "old" };
        }

        return doc;
      })
    );
  };

  // ============================================================================
  // FILE VALIDATION
  // ============================================================================
  const isValidFileFormat = (files) => {
    return files.some((file) => {
      const fileExtension = `.${file.name.split(".").pop()}`;
      return (
        ACCEPTED_FILE_FORMATS.includes(file.type) ||
        ACCEPTED_FILE_FORMATS.includes(fileExtension)
      );
    });
  };

  // ============================================================================
  // FILE HANDLING
  // ============================================================================
  const handleDrop = (files, label) => {
    if (!isValidFileFormat(files)) {
      return;
    }

    const fileExtension = files[0].name.split(".").pop();

    setDocRequired((prevDocRequired) =>
      prevDocRequired.map((data) => {
        if (data.label === label) {
          return {
            ...data,
            fileType: fileExtension,
            file: files[0],
            docType: data.internalLabel,
            docUpdate: "new",
          };
        }
        return data;
      })
    );
  };

  const handleRemoveButton = (label) => {
    setDocRequired((prevDocRequired) =>
      prevDocRequired.map((data) => {
        if (data.label === label) {
          return {
            ...data,
            fileType: "",
            file: null,
            docType: "",
            docUpdate: "",
          };
        }
        return data;
      })
    );
  };

  // ============================================================================
  // FILE UPLOAD API
  // ============================================================================
  const uploadSingleFile = async (file, fileName, fileType, docType, side) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await documentPurposeUpload(
        transId,
        studentUserId,
        fileName,
        fileType,
        docType,
        formData,
        side
      );
      return response;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const submitPreviouslyUploadedDocs = async () => {
    const oldDocsArray = docRequired
      .filter((doc) => doc.docUpdate === "old")
      .map((doc) => {
        const matchingUploadedDoc = uploadedDocs.find(
          (uploadedDoc) => uploadedDoc.docType === doc.internalLabel
        );

        if (matchingUploadedDoc) {
          return {
            docId: matchingUploadedDoc.docId,
            docType: doc.internalLabel,
            side: "front",
          };
        }

        return null;
      })
      .filter(Boolean);

    if (oldDocsArray.length > 0) {
      const body = {
        transactionId: transId,
        docs: oldDocsArray,
      };
      await submitUploadedDocuments(body);
    }
  };

  const uploadAllFiles = async () => {
    setErrorMessage("");
    setLoader(true);
    setButtonDisabled(true);

    try {
      // Upload all new files
      const uploadPromises = docRequired.map(async (data) => {
        if (
          data.docUpdate === "new" &&
          data.file &&
          Object.keys(data.file).length > 0
        ) {
          // For re-upload, use the side from document data; otherwise use getDocumentSideAndType
          let side = data.side || "";
          let docType = data.internalLabel || data.docType;

          // If no side is set (normal flow), determine from docType
          if (!side) {
            const sideAndType = getDocumentSideAndType(data.docType);
            side = sideAndType.side;
            docType = sideAndType.docType;
          }

          await uploadSingleFile(
            data.file,
            data.label,
            data.fileType,
            docType,
            side
          );
        }
      });

      await Promise.all(uploadPromises);
      await submitPreviouslyUploadedDocs();

      // Handle next step based on upload type
      if (isReupload) {
        handleDocumentReupload();
      } else {
        generateA2SignUrl();
        setErrorMessage("");
      }
    } catch (error) {
      setLoader(false);
      setErrorMessage("Error while uploading file. Please try again.");
    }
  };

  const getDocumentSideAndType = (docType) => {
    let side = "";
    let updatedDocType = docType;

    if (docType === DOC_TYPES.PASSPORT_FRONT) {
      side = "front";
      updatedDocType = DOC_TYPES.PASSPORT;
    } else if (docType === DOC_TYPES.PASSPORT_BACK) {
      side = "back";
      updatedDocType = DOC_TYPES.PASSPORT;
    }

    return { side, docType: updatedDocType };
  };

  // ============================================================================
  // DOCUMENT RE-UPLOAD
  // ============================================================================
  const handleDocumentReupload = async () => {
    setErrorMessage("");

    try {
      const response = await reUploadOfDocuments(transId, redirectionLink);

      if (response.status === 200) {
        // Extract eSign URL from reupload response
        const eSignUrl = response.data?.eSignUrl;

        if (eSignUrl) {
          setA2signUrl(eSignUrl);
          setGeneratedA2(true);
          setLoader(false);
          setButtonDisabled(false);
          setMessageToDisplay(
            "You will be redirected to sign the A2 form. Kindly click in case of failure to redirect."
          );
        }
        setErrorMessage("");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      setButtonDisabled(false);
      setLoading(false);
      setLoader(false);
      setErrorMessage("Something went wrong. Please try again later.");
    }
  };

  // ============================================================================
  // A2 FORM SIGNING
  // ============================================================================
  const generateA2SignUrl = async () => {
    const body = {
      transactionId: transId,
      redirectUrl:redirectionLink
    };

    try {
      const response = await generateA2SignApi(body);

      if (response.status === 200) {
        // window.open(response.data.esignUrl, "_blank");
        setA2signUrl(response.data.esignUrl);
        setGeneratedA2(true);
        setMessageToDisplay(
          "You will be redirected to sign the A2 form. Kindly click in case of failure to redirect."
        );
      } else {
        throw new Error("Failed to generate A2 sign URL");
      }
    } catch (error) {
      setLoader(false);
      setButtonDisabled(false);
      setErrorMessage("Something went wrong. Please try again later.");
    }
  };

  const openA2FormButton = () => {
    window.open(a2SignUrl, "_blank");
  };

  // ============================================================================
  // PAYMENT LINK GENERATION
  // ============================================================================
  const generatePaymentLink = async () => {
    setShowSpinner(true);

    const body = {
      transactionId: transId,
      redirectUrl: redirectionLink,
    };

    try {
      const response = await paymentLink(body);

      if (response.status === 200 && response.data.paymentLink) {
        setLoader(false);
        setPayNowButton(false);
        window.open(response.data.paymentLink, "_blank");
        setPaymentLinkGenerated(true);
        setMessageToDisplay(
          "Your pop-up blocker may be preventing you from proceeding. To continue, please disable your pop-up blocker."
        );
        setErrorMessage("");
      } else {
        handlePaymentLinkFailure();
      }
    } catch (error) {
      handlePaymentLinkFailure();
    }
  };

  const handlePaymentLinkFailure = () => {
    if (isQrRequired === "true") {
      sessionStorage.setItem("nextStep", "payment");
      navigate("/payment");
    } else {
      setLoader(false);
      setButtonDisabled(false);
      setPaymentLinkGenerated(true);
      setErrorMessage(
        <>
          We are unable to redirect you to the payment link. For further
          details, contact support.{" "}
          <a href="/transaction-detail">Transaction Detail</a>
        </>
      );
    }
  };

  // ============================================================================
  // TRANSACTION STATUS POLLING
  // ============================================================================
  useEffect(() => {
    if (generatedA2 || paymentLinkGenerated) {
      const intervalId = setInterval(() => {
        checkTransactionStatus();
      }, POLLING_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [generatedA2, paymentLinkGenerated]);

  const checkTransactionStatus = async () => {
    try {
      const response = await getTransactionDetailUsingId(transId);

      if (response.status === 200) {
        const status = response.data?.transactionData?.status;
        if (generatedA2) {
          handleA2SigningStatus(status);
        } else if (paymentLinkGenerated) {
          handlePaymentStatus(status);
        }
      }
    } catch (error) {
      console.error("Error checking transaction status:", error);
    }
  };

  const handleA2SigningStatus = (status) => {
    switch (status) {
      case TRANSACTION_STATUS.MANUAL_REVIEW:
        sessionStorage.setItem("Reupload", false);
        setShowButton(false);
        setGeneratedA2(true);
        setIsManualReview(true);
        setMessageToDisplay(
          "Your documents are under review. Please wait for a moment or you can come back to the flow from the transaction list once approved."
        );
        setShowSpinner(true);
        break;

      case TRANSACTION_STATUS.VERIFIED:
                sessionStorage.setItem("Reupload", false);
        setShowButton(true);
        setGeneratedA2(false);
        setPayNowButton(true);
        setPaymentLinkGenerated(true);

        setIsManualReview(false);
        setMessageToDisplay(
          "Your documents are verified successfully. Please complete your payment."
        );
        break;

      case TRANSACTION_STATUS.A2_E_SIGNED:
        sessionStorage.setItem("Reupload", false);
        setPayNowButton(true);
        setMessageToDisplay(
          "Thank you for completing your A2 form signing. Please complete your payment by clicking the button below."
        );
        break;
      case TRANSACTION_STATUS.REJECTED:
        const  reupload = JSON.parse(sessionStorage.getItem("Reupload"));
        if (!isReupload && !reupload){
          setShowButton(false);
          setGeneratedA2(false);
          setIsManualReview(false);
          // setPayNowButton(false);
          setShowNavigateToTransactionButton(true);
          setMessageToDisplay(
            "Your documents have been rejected. Please reupload them by navigating to transaction list screen."
          );
        }
        break;

      default:
        break;
    }
  };

  const handlePaymentStatus = (status) => {
    if (status === TRANSACTION_STATUS.CANCELLED) {
      setShowSpinner(false);
      setTransactionStatusIcon("cancelled");
      setMessageToDisplay(<div>Your transaction has been cancelled.</div>);

      setTimeout(() => {
        navigate("/transaction-detail", { replace: true });
        window.location.reload();
      }, REDIRECT_DELAY);
    } else if (
      status !== TRANSACTION_STATUS.A2_E_SIGNED &&
      status !== TRANSACTION_STATUS.WAITING_FOR_FUND &&
      status !== TRANSACTION_STATUS.VERIFIED // Don't treat VERIFIED as payment success
    ) {
      setShowSpinner(false);
      setTransactionStatusIcon("success");
      setMessageToDisplay(
        <div>You have successfully initiated the payment.</div>
      );

      setTimeout(() => {
        navigate("/transaction-detail", { replace: true });
        window.location.reload();
      }, REDIRECT_DELAY);
    }
  };

  // ============================================================================
  // VALIDATE ALL FILES UPLOADED
  // ============================================================================
  useEffect(() => {
    validateAllFilesUploaded();
  }, [docRequired, visaCheckBox, uploadedDocs]);

  const validateAllFilesUploaded = () => {
    const isStudentAbroadPurpose = purposeId === STUDENT_ABROAD_PURPOSE_ID;

    const areAllFilesUploaded = docRequired.every((data) => {
      // Special handling for student visa
      if (
        isStudentAbroadPurpose &&
        data.name === DOC_TYPES.STUDENT_VISA &&
        !isReupload
      ) {
        if (
          data.docUpdate === "old" ||
          (data.file && Object.keys(data.file).length > 0)
        ) {
          setVisaCheckBox(true);
        }
        return true;
      }

      // Check if required document is uploaded
      return data.isRequired && data.docUpdate !== "old"
        ? data.file && Object.keys(data.file).length > 0
        : true;
    });

    // Determine button disabled state
    if (isStudentAbroadPurpose && showStudentVisa && !isReupload) {
      setButtonDisabled(!(areAllFilesUploaded && visaCheckBox));
    } else {
      setButtonDisabled(!areAllFilesUploaded);
    }
  };

  // ============================================================================
  // RENDER: STATUS MESSAGE SCREEN
  // ============================================================================
  const renderStatusScreen = () => (
    <div className={styles.paymentWaiting}>
      {errorMessage.length === 0 ? (
        <>
          {showSpinner ? (
            <Spinner
              animation="border"
              className={styles.spinnerColor}
              style={{
                width: "28px",
                height: "28px",
                borderColor: themeColor,
                borderRightColor: "#eaecf0",
              }}
            />
          ) : transactionStatusIcon !== "" ? (
            <img
              src={
                transactionStatusIcon === "success"
                  ? tickIcon
                  : transactionStatusIcon === "cancelled"
                  ? crossIcon
                  : ""
              }
              alt={transactionStatusIcon}
              style={{ height: "75px", width: "75px" }}
            />
          ) : null}

          <p className={styles.message}>{messageToDisplay}</p>

          {isManualReview && (
            <Button
              onClick={() => {
                navigate("/transaction-detail");
              }}
            >
              Navigate to transaction
            </Button>
          )}

          {showNavigateToTransactionButton && (
            <Button
              onClick={() => {
                navigate("/transaction-detail");
              }}
            >
              Navigate to transaction
            </Button>
          )}

          {showButton &&
            (generatedA2 ? (
              <Button onClick={openA2FormButton}>E-Sign A2 form</Button>
            ) : showPayNowButton ? (
              <Button onClick={generatePaymentLink}>Pay Now</Button>
            ) : null)}
        </>
      ) : (
        <p className={styles.message}>{errorMessage}</p>
      )}
    </div>
  );

  // ============================================================================
  // RENDER: DOCUMENT UPLOAD SCREEN
  // ============================================================================
  const renderDocumentUploadScreen = () => (
    <>
      <div style={{ marginTop: "20px" }}>
        {loading ? (
          <Spinner
            animation="border"
            className={styles.spinnerColorOutside}
            style={{
              borderColor: themeColor,
              borderRightColor: "#eaecf0",
            }}
          />
        ) : (
          <Row style={{ marginBottom: "8px", justifyContent: "center" }}>
            {docRequired.map((data, index) => {
              const uploadedFile = Array.isArray(uploadedDocs)
                ? uploadedDocs.find((doc) => doc.docType === data.internalLabel)
                : null;

              return (
                <Col
                  key={index}
                  md={3}
                  xs={8}
                  lg={3}
                  className={styles.fileStyling}
                >
                  <FileUploader
                    onDrop={handleDrop}
                    acceptedFormats={ACCEPTED_FILE_FORMATS}
                    maxSize={MAX_FILE_SIZE}
                    name={data.label}
                    label={data.name}
                    sublabel={
                      data.isRequired === false &&
                      data.name === DOC_TYPES.STUDENT_VISA
                        ? "(Mandatory if student is currently residing abroad)"
                        : data.isRequired === false
                        ? "(Optional)"
                        : ""
                    }
                    loading={loader}
                    onRemoveButton={handleRemoveButton}
                    preUploadedFile={
                      uploadedFile ? uploadedFile.docUrl_1 : null
                    }
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* Allowed Formats */}
      <Row className={styles.allowedFormat}>
        <p className={styles.allowedFormatheading}>Allowed Formats</p>
        <p className={styles.allowedFormatsubheading}>
          JPEG, PNG, JPG or PDF (max. size 5MB)
        </p>
      </Row>

      {/* Student Visa Checkbox */}
      {purposeId === STUDENT_ABROAD_PURPOSE_ID &&
        showStudentVisa &&
        !isReupload && (
          <Row className={styles.legalBox} style={{ border: "0px" }}>
            <div className={styles.checkMark}>
              <input
                className={styles.checkbox}
                type="checkbox"
                id="flexCheckDefault"
                checked={visaCheckBox}
                onChange={() => setVisaCheckBox(!visaCheckBox)}
              />
              <label className={styles.checklabel} htmlFor="flexCheckDefault">
                If the student is currently residing abroad, I understand that
                Student's VISA is mandatory to upload.
              </label>
            </div>
          </Row>
        )}

      {/* Pre-uploaded Documents Warning */}
      {uploadedDocs.length > 0 && (
        <p
          className={styles.allowedFormatsubheading}
          style={{ marginTop: "10px", marginBottom: "0px" }}
        >
          <span style={{ color: "red", fontSize: "16px" }}>*</span>
          Please review the pre-uploaded documents before proceeding.
        </p>
      )}

      {/* Error Message */}
      {errorMessage !== "" && (
        <p className={styles.errorMessage}>{errorMessage}</p>
      )}

      {/* Confirm Upload Button */}
      <Row className={styles.ProceedButtton}>
        <CommonButton
          title="Confirm Upload"
          onClick={uploadAllFiles}
          loading={loader}
          disabled={buttonDisabled || loading}
          width={"261px"}
        />
      </Row>
    </>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  const showStatusScreen =
    paymentLinkGenerated ||
    generatedA2 ||
    showPayNowButton ||
    showNavigateToTransactionButton;

  return (
    <div>
      {/* Header Section */}
      {!paymentLinkGenerated && (
        <>
          <HeadWithSubHead
            head={`${isReupload ? "Document Re-Upload" : "Document Upload"}`}
            subHead="Upload the Required Docs related to your purpose"
          />
          {!isReupload && (
            <>
              <p className={styles.transferMoneyHeading1}>
                Transfer Purpose -{" "}
                <span className={styles.transferMoneySubHeading}>
                  {purpose} {subPurpose ? `(${subPurpose})` : ""}
                </span>
              </p>

              <p className={styles.transferMoneyHeading2}>
                Transaction ID -{" "}
                <span className={styles.transferMoneySubHeading}>
                  {transId}
                </span>
              </p>
            </>
          )}
        </>
      )}

      {/* Main Content Card */}
      <Card className={styles.cardBorder}>
        {showStatusScreen ? renderStatusScreen() : renderDocumentUploadScreen()}
      </Card>
    </div>
  );
};

export default DocUpload;
